import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApiError, Dashboard, DashboardShow, DrillSpec } from '../api';
import { dashboardEndpoints } from '../api';
import { DrillPanel } from './Reports';
import { Badge, EmptyRow, ErrorNote, Panel, Table } from '../ui';
import type { Tone } from '../ui';
import { dollars, inZone } from './event/shared';
import { dateWords } from '../terms';

/**
 * Admin → home (D-196).
 *
 * The index route was the events list, so the first thing anybody saw on
 * arriving was a catalog rather than an answer to the question they came with.
 *
 * **Counts that link out, never a second copy of another page.** Every number
 * here is a door into the screen that already owns that detail — a dashboard
 * that re-renders the orders table is a second place for the same thing to go
 * wrong, and the one people stop trusting first. Refreshes on a timer while
 * it is open, because on a show night it is left open.
 */

const SEVERITY: Record<string, Tone> = { INFO: 'neutral', WARNING: 'warn', ERROR: 'bad', CRITICAL: 'bad' };

// `detail` and `to` are genuinely absent on some of these, and the project
// compiles with exactOptionalPropertyTypes, so the undefined is spelled out.
/**
 * One figure, and where its rows are (item 167, D-304).
 *
 * The reports have drilled since D-272 and the dashboard never did, so every
 * number on the first screen anybody opens ended in a SQL question. `onDrill`
 * opens the same panel the reports use — not a copy of it, so there is one
 * drill vocabulary and one place it can go wrong.
 *
 * A figure with no rows underneath it stays plain. Inventing a drill for a
 * waiting refund request would mean a click that lands somewhere unrelated,
 * which is worse than a number that does not invite one.
 */
function Stat({ label, value, detail, tone = '', to, onDrill }: { label: string; value: string; detail?: string | undefined; tone?: string | undefined; to?: string | undefined; onDrill?: (() => void) | undefined }) {
  const body = (
    <>
      <div className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</div>
      <div className={`tabular mt-1 text-2xl font-bold ${tone || 'text-ink-900'}`}>{value}</div>
      {detail ? <div className="mt-0.5 text-xs text-ink-500">{detail}</div> : null}
    </>
  );
  if (onDrill) {
    return (
      <button type="button" onClick={onDrill} className="block w-full rounded px-1 py-1 text-left hover:bg-mist" title="Click for the rows behind this number">
        {body}
      </button>
    );
  }
  return to ? (
    <Link to={to} className="block rounded px-1 py-1 hover:bg-mist">
      {body}
    </Link>
  ) : (
    <div className="px-1 py-1">{body}</div>
  );
}

/** How full a show is, as a bar, because a fraction is easier to read than two numbers. */
function Fullness({ show }: { show: DashboardShow }) {
  if (show.capacity === 0) return <span className="text-xs text-ink-400">No inventory yet</span>;
  const pct = Math.round((show.sold / show.capacity) * 100);
  return (
    <div className="min-w-40">
      <div className="flex justify-between text-xs">
        <span className="tabular text-ink-700">
          {show.sold} / {show.capacity}
        </span>
        <span className="tabular text-ink-400">{pct}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full bg-mist">
        <div className="h-1.5 bg-accent" style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {show.in_flight > 0 ? <div className="mt-0.5 text-xs text-ink-400">{show.in_flight} in carts right now</div> : null}
    </div>
  );
}

const hoursUntil = (iso: string): number => Math.round((new Date(iso).getTime() - Date.now()) / 3_600_000);

export default function DashboardPage() {
  const dashboard = useQuery<Dashboard, ApiError>({
    queryKey: ['dashboard'],
    queryFn: () => dashboardEndpoints.get(),
    refetchInterval: 60_000,
  });
  /** The rows behind a figure, in the panel the reports already use (D-304). */
  const [drillSpec, setDrillSpec] = useState<DrillSpec | null>(null);

  const d = dashboard.data;
  const change = d && d.sales.yesterday.gross_cents > 0 ? Math.round(((d.sales.today.gross_cents - d.sales.yesterday.gross_cents) / d.sales.yesterday.gross_cents) * 100) : null;

  return (
    <div className="space-y-4">
      {dashboard.error ? <ErrorNote>{dashboard.error.message}</ErrorNote> : null}

      <Panel title="Today" description={d ? `Business day ${d.business_date} · ${d.timezone}` : 'Loading…'}>
        <div className="flex flex-wrap gap-8 px-5 py-4">
          <Stat
            label="Sold today"
            value={d ? dollars(d.sales.today.gross_cents) : '—'}
            detail={d ? `${d.sales.today.orders} orders · ${d.sales.today.tickets} tickets` : undefined}
            onDrill={d?.sales.today.gross_drill ? () => setDrillSpec(d.sales.today.gross_drill!) : undefined}
          />
          <Stat
            label="Yesterday"
            value={d ? dollars(d.sales.yesterday.gross_cents) : '—'}
            detail={change === null ? undefined : `${change >= 0 ? '+' : ''}${change}% today`}
            tone={change === null ? '' : change >= 0 ? 'text-ok' : 'text-ink-900'}
          />
          <Stat
            label="Refunds waiting"
            value={d ? String(d.attention.refund_requests) : '—'}
            detail={d?.attention.oldest_refund_request ? `oldest ${Math.abs(hoursUntil(d.attention.oldest_refund_request))}h ago` : undefined}
            tone={d && d.attention.refund_requests > 0 ? 'text-warn' : ''}
            to="/orders?refund_requested=1"
          />
          <Stat
            label="Open chargebacks"
            value={d ? String(d.attention.open_disputes) : '—'}
            detail={d?.attention.soonest_evidence_due ? `evidence due in ${hoursUntil(d.attention.soonest_evidence_due)}h` : undefined}
            tone={d && d.attention.open_disputes > 0 ? 'text-warn' : ''}
            to="/disputes"
          />
        </div>
      </Panel>

      {d && d.alerts.length > 0 ? (
        <Panel title="Unacknowledged alerts" description="Recorded by the system itself. These do not clear on their own.">
          <Table headers={['When', 'Severity', 'What']}>
            {d.alerts.map((alert) => (
              <tr key={alert.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{inZone(alert.created_at, d.timezone)}</td>
                <td className="px-5 py-3">
                  <Badge tone={SEVERITY[alert.severity] ?? 'neutral'}>{alert.severity}</Badge>
                </td>
                <td className="px-5 py-3 text-sm">
                  <div className="text-ink-900">{alert.message}</div>
                  <div className="font-mono text-xs text-ink-400">{alert.code}</div>
                </td>
              </tr>
            ))}
          </Table>
        </Panel>
      ) : null}

      <Panel title="Tonight and tomorrow" description="Everything on sale from twelve hours ago to a day out. Refreshes itself every minute.">
        <Table headers={['Show', 'Starts', 'Status', 'Sold', '']}>
          {!d ? (
            <EmptyRow columns={5}>Loading…</EmptyRow>
          ) : d.shows.length === 0 ? (
            <EmptyRow columns={5}>Nothing scheduled in the next day.</EmptyRow>
          ) : (
            d.shows.map((show) => (
              <tr key={show.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <Link to={`/events/${show.event_id}`} className="font-medium text-accent hover:underline">
                    {show.title}
                  </Link>
                  <div className="text-xs text-ink-400">{show.venue_name ?? '—'}</div>
                </td>
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">
                  {inZone(show.starts_at, show.timezone)}
                  {show.doors_at ? <div className="text-xs text-ink-400">{dateWords().gates.toLowerCase()} {inZone(show.doors_at, show.timezone)}</div> : null}
                </td>
                <td className="px-5 py-3">
                  <Badge tone={show.status === 'ON_SALE' ? 'ok' : 'neutral'}>{show.status}</Badge>
                </td>
                <td className="px-5 py-3">
                  <Fullness show={show} />
                </td>
                <td className="px-5 py-3 text-right text-sm">
                  <Link to={`/door?performance=${show.id}`} className="text-accent hover:underline">
                    Door
                  </Link>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>

      {drillSpec ? <DrillPanel spec={drillSpec} onClose={() => setDrillSpec(null)} /> : null}
    </div>
  );
}
